/**
 * DeepLinkPage.tsx — 1904va
 * Handles ?t= token from WhatsApp/Telegram/SMS notifications
 * Validates token → sets participant session → navigates to destination
 * Zero friction — user taps notification and lands directly on content
 */
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { Spinner } from '@/components/ui'

type State = 'loading' | 'success' | 'expired' | 'used' | 'invalid' | 'error'

export default function DeepLinkPage() {
  const [searchParams] = useSearchParams()
  const navigate       = useNavigate()
  const { setParticipant } = useAuthStore()
  const [state, setState]  = useState<State>('loading')
  const [message, setMsg]  = useState('')

  useEffect(() => {
    const token = searchParams.get('t')
    if (!token) { setState('invalid'); setMsg('No access token found.'); return }

    const validate = async () => {
      try {
        // Call token-auth Edge Function directly with query params
        const res = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/token-auth?action=validate&token=${encodeURIComponent(token)}`,
          {
            headers: {
              apikey:        import.meta.env.VITE_SUPABASE_ANON_KEY,
              Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            },
          }
        )

        const result = await res.json()

        if (!result.valid) {
          setState(result.reason === 'expired' ? 'expired' : result.reason === 'already_used' ? 'used' : 'invalid')
          setMsg(
            result.reason === 'expired'      ? 'This link has expired. Links are valid for 24 hours.' :
            result.reason === 'already_used' ? 'This link has already been used.' :
            'This link is not valid.'
          )
          return
        }

        // Set participant in store — app will treat them as authenticated
        if (result.participant) {
          setParticipant(result.participant)
        }

        setState('success')

        // Navigate to destination (e.g. /inbox?highlight=abc123)
        const destination = result.destination ?? '/inbox'
        const dest = result.media_item_id
          ? `${destination}?highlight=${result.media_item_id}`
          : destination

        setTimeout(() => navigate(dest, { replace: true }), 600)

      } catch (err) {
        console.error('Token validation error:', err)
        setState('error')
        setMsg('Something went wrong. Please try signing in.')
      }
    }

    validate()
  }, [])

  const icon = {
    loading: null,
    success: '✅',
    expired: '⏰',
    used:    '🔒',
    invalid: '❌',
    error:   '⚠️',
  }[state]

  return (
    <div className="min-h-screen bg-room-bg flex items-center justify-center px-6">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-room-accent/6 rounded-full blur-[120px]" />
      </div>

      <div className="relative text-center space-y-5 max-w-xs w-full">
        <h1 className="text-3xl font-black">
          <span className="bg-gradient-to-br from-purple-300 via-room-accent to-purple-500 bg-clip-text text-transparent">
            The Room
          </span>
        </h1>

        {state === 'loading' ? (
          <div className="space-y-3">
            <Spinner size={32} />
            <p className="text-neutral-400 text-sm">Signing you in…</p>
          </div>
        ) : state === 'success' ? (
          <div className="space-y-2">
            <div className="text-5xl">{icon}</div>
            <p className="text-white font-semibold">You're in</p>
            <p className="text-neutral-400 text-sm">Taking you to your content…</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-5xl">{icon}</div>
            <p className="text-neutral-300 text-sm">{message}</p>
            <button
              onClick={() => navigate('/auth', { replace: true })}
              className="w-full py-3 rounded-xl bg-room-accent hover:bg-fuchsia-600 text-white font-semibold text-sm transition-colors"
            >
              Sign in instead
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
